import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV, ORG } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AnchorDialog } from "@/components/AnchorDialog";

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Primary">
      {NAV.map((item) => {
        const Icon = Icons[item.icon];
        return (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.path === "/"}
            onClick={onNavigate}
            data-testid={`nav-${item.id}`}
            className={({ isActive }) =>
              `group flex min-h-[44px] items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "border-cyan-500/50 bg-slate-900/90 text-cyan-300"
                  : "border-transparent text-slate-400 hover:border-slate-700 hover:bg-slate-900/60 hover:text-slate-200"
              }`
            }
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative flex h-9 w-9 items-center justify-center rounded-md border border-cyan-500/40 bg-slate-950">
        <Icons.Radio className="h-5 w-5 text-cyan-400" aria-hidden="true" />
        <span className="signal-pulse" aria-hidden="true" />
      </div>
      <div className="leading-tight">
        <p className="font-heading text-sm font-extrabold uppercase tracking-tight text-slate-100">SignalLink AI</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-500">CAGE {ORG.cage}</p>
      </div>
    </div>
  );
}

export default function Layout() {
  const { settings, toggleAdaMode, stats } = useApp();
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const current = NAV.find((n) => n.path === location.pathname);

  return (
    <div className="min-h-screen bg-[#070A0F] text-slate-200">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#070A0F]/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="border-slate-700 text-slate-300 lg:hidden" aria-label="Open navigation menu" data-testid="mobile-menu-button">
                  <Icons.Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] border-slate-800 bg-[#0D121D] p-0">
                <div className="border-b border-slate-800 p-4"><Brand /></div>
                <div className="max-h-[calc(100vh-80px)] overflow-y-auto p-3">
                  <NavItems onNavigate={() => setMenuOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <Brand />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden items-center gap-2 rounded-md border border-slate-800 bg-slate-950/60 px-3 py-1.5 md:flex" data-testid="system-status-pill">
              <span className={`h-2 w-2 rounded-full ${stats.systemStatus === "OPERATIONAL" ? "bg-emerald-400" : "bg-rose-400"}`} aria-hidden="true" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-slate-300">{stats.systemStatus}</span>
            </div>

            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-800 bg-slate-950/60 px-2.5 py-1.5" htmlFor="ada-toggle">
              <Icons.Accessibility className="h-4 w-4 text-cyan-400" aria-hidden="true" />
              <span className="hidden font-mono text-[11px] uppercase tracking-wider text-slate-300 sm:inline">ADA Tier 1-A</span>
              <Switch id="ada-toggle" checked={settings.adaMode} onCheckedChange={toggleAdaMode} data-testid="ada-mode-toggle" aria-label="Toggle ADA Tier 1-A Interface Mode" />
            </label>

            <Button onClick={() => setAnchorOpen(true)} className="bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400 glow-cyan" data-testid="topbar-anchor-button">
              <Icons.Anchor className="mr-0 h-4 w-4 sm:mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">Anchor Event</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        {/* Desktop sidebar */}
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 overflow-y-auto border-r border-slate-800 p-3 lg:block">
          <NavItems />
          <div className="mt-4 rounded-md border border-slate-800 bg-slate-950/40 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Operating Principle</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">{ORG.principle}</p>
          </div>
        </aside>

        {/* Main content */}
        <main id="main-content" className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8">
          {current && (
            <p className="mb-3 hidden font-mono text-[11px] uppercase tracking-widest text-slate-600 lg:block">
              SignalLink AI / {current.label}
            </p>
          )}
          <Outlet />
          <footer className="mt-12 border-t border-slate-800 pt-5 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
              {ORG.name} · CAGE {ORG.cage} · {ORG.framework} · {ORG.crypto}
            </p>
          </footer>
        </main>
      </div>

      <AnchorDialog open={anchorOpen} onOpenChange={setAnchorOpen} />
    </div>
  );
}
